import { useEffect, useRef, useContext } from 'react';
import axios from '../utils/axiosConfig';
import { AuthContext } from '../authContetxt';

/**
 * Auto-logout when user is idle (no activity) for `idleMinutes`.
 * - On any activity, sends a THROTTLED /api/auth/ping to extend the server session.
 * - If idle timeout elapses, calls `logout()` to revoke server session and redirect to /login.
 */
export default function useIdleAutoLogout({ idleMinutes = 30, pingThrottleMs = 60_000 } = {}) {
  const { isAuthenticated, logout } = useContext(AuthContext);
  const lastActivityRef = useRef(Date.now());
  const lastPingRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousemove','keydown','click','scroll','touchstart'];
    const onActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;

      // Throttle /ping so we don't spam; extends server-side session only when active
      if (now - lastPingRef.current >= pingThrottleMs) {
        lastPingRef.current = now;
        axios.get('/api/auth/ping').catch(() => {/* 401 handled globally in axiosConfig */});
      }

      // Reset the idle timer
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Idle window elapsed → proactively logout (server + client)
        logout(); // uses your AuthContext logout, which POSTs /api/auth/logout and redirects
      }, idleMinutes * 60 * 1000);
    };

    // Prime the timer on mount
    onActivity();

    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, idleMinutes, pingThrottleMs, logout]);
}
